import { type FC, useState } from 'react';

import { type IPartyCustomization } from '../../../../interface/ICustomization';
import { hexToRgb, onBrandColor } from '../../../../theme/applyPartyTheme';
import { parseVideoUrl } from '../../../../utils/parseVideoUrl';
import {
	VideoBrandedFrame,
	VideoCardLink,
	VideoLabel,
	VideoOverlay,
	VideoPlayBadge,
	VideoThumbImg,
} from './VideoThumbnailCard.styled';

// House green as a safety net when the party primary is missing/invalid, so the
// play badge / branded frame is coloured correctly on first paint (before
// applyPartyTheme's CSS-var effect has run).
const FALLBACK_PRIMARY = '#1e5631';

const PlayIcon = () => (
	<svg
		viewBox='0 0 24 24'
		fill='currentColor'
		aria-hidden='true'
	>
		<path d='M8 5v14l11-7z' />
	</svg>
);

interface VideoThumbnailCardProps {
	customization: IPartyCustomization;
}

const VideoThumbnailCard: FC<VideoThumbnailCardProps> = ({ customization }) => {
	const rawUrl = customization.videoUrl ?? '';
	const parsed = parseVideoUrl(rawUrl);
	// A YouTube thumbnail can fail to load (e.g. private/removed video) — fall
	// back to the branded frame rather than showing a broken image.
	const [thumbBroken, setThumbBroken] = useState(false);

	// Not a recognisable video URL → render nothing (the card is dead weight).
	if (!parsed) return null;

	const primary =
		customization.primaryColor && hexToRgb(customization.primaryColor) ? customization.primaryColor : FALLBACK_PRIMARY;
	const onColor = onBrandColor(primary);
	const rgba = (hex: string, alpha: number): string => {
		const c = hexToRgb(hex);
		return c ? `rgba(${c.r}, ${c.g}, ${c.b}, ${alpha})` : hex;
	};

	const showThumb = Boolean(parsed.thumbnailUrl) && !thumbBroken;

	return (
		<VideoCardLink
			href={rawUrl}
			target='_blank'
			rel='noopener noreferrer'
			aria-label='Watch the video (opens in a new tab)'
		>
			{showThumb ? (
				<VideoThumbImg
					src={parsed.thumbnailUrl}
					alt=''
					loading='lazy'
					decoding='async'
					onError={() => setThumbBroken(true)}
				/>
			) : (
				<VideoBrandedFrame
					aria-hidden='true'
					style={{ background: `linear-gradient(135deg, ${rgba(primary, 0.95)} 0%, ${rgba(primary, 0.7)} 100%)` }}
				/>
			)}
			<VideoOverlay aria-hidden='true' />
			<VideoPlayBadge
				aria-hidden='true'
				style={{ background: primary, color: onColor }}
			>
				<PlayIcon />
			</VideoPlayBadge>
			<VideoLabel>Watch the video</VideoLabel>
		</VideoCardLink>
	);
};

export default VideoThumbnailCard;
